import { defineNuxtPlugin, useRouter, nextTick } from '#imports'
import mixpanel from 'mixpanel-browser'
import posthog from 'posthog-js'
import VueHotjar from 'vue-hotjar-next'

export default defineNuxtPlugin((nuxtApp) => {
  const enableTracking = () => {
    if (!isTrackingEnabled()) return

    const config = useRuntimeConfig().public

    mixpanel.init(config.MIXPANEL_TOKEN, {
      api_host: 'https://api-eu.mixpanel.com',
      ip: false,
    })

    if (config.POSTHOG_PUBLIC_KEY) {
      posthog.init(config.POSTHOG_PUBLIC_KEY as string, {
        api_host: config.POSTHOG_HOST as string,
        capture_pageview: false, // we add manual pageview capturing below
        capture_pageleave: true, // automatically capture a pageleave event when the user leaves the site or closes the tab
        person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
        loaded: (posthog) => {
          if (import.meta.env.MODE === 'development') posthog.debug()
        },
      })

      useRouter().afterEach((to) => {
        nextTick(() => {
          posthog.capture('$pageview', {
            current_url: to.fullPath,
          })
        })
      })
    }

    if (config.HOTJAR_ID) {
      nuxtApp.vueApp.use(VueHotjar, {
        id: Number(config.HOTJAR_ID),
        isProduction: config.HOTJAR_ENABLED,
        snippetVersion: 6,
      })
    }
  }

  const trackEvent = (event: string, params = {}) => {
    if (isTrackingEnabled()) {
      mixpanel.track(event, params)
      posthog.capture(event, params)
    }
  }

  const setTrackingUser = (email?: string) => {
    if (isTrackingEnabled() && !!email) {
      mixpanel.identify()
      mixpanel.people.set({
        $email: email,
      })
    }
  }

  enableTracking()

  return {
    provide: {
      enableTracking: enableTracking,
      trackEvent: trackEvent,
      setTrackingUser: setTrackingUser,
    },
  }
})

function isTrackingEnabled() {
  const config = useRuntimeConfig()
  const consentCookie = useCookie('cc')

  return (
    !!config.public.MIXPANEL_TOKEN &&
    !!consentCookie.value &&
    !!consentCookie.value['analytics']
  )
}
